import { useEffect, useRef } from "react";

/**
 * Multi-layered JARVIS Orb with 4 states: idle | listening | thinking | speaking
 * Audio-synced wave bars when speaking.
 */
export default function JarvisOrb({ state = "idle", size = 360, onClick, audioLevel = 0 }) {
  const containerRef = useRef(null);

  const config = {
    idle: {
      core: "from-cyan-500 via-sky-400 to-cyan-200",
      ring: "border-cyan-500/40",
      glow: "rgba(0, 212, 255, 0.5)",
      pulseClass: "animate-orb-pulse",
    },
    listening: {
      core: "from-sky-400 via-cyan-300 to-white",
      ring: "border-cyan-300/60",
      glow: "rgba(56, 189, 248, 0.7)",
      pulseClass: "animate-orb-pulse-fast",
    },
    thinking: {
      core: "from-indigo-500 via-cyan-400 to-sky-300",
      ring: "border-indigo-400/60",
      glow: "rgba(129, 140, 248, 0.7)",
      pulseClass: "animate-orb-pulse-fast",
    },
    speaking: {
      core: "from-cyan-400 via-sky-300 to-white",
      ring: "border-cyan-400/80",
      glow: "rgba(34, 211, 238, 0.9)",
      pulseClass: "animate-orb-pulse-fast",
    },
  }[state];

  const scale = 1 + audioLevel * 0.15;

  return (
    <div
      ref={containerRef}
      data-testid={`jarvis-orb-${state}`}
      onClick={onClick}
      className="relative flex items-center justify-center cursor-pointer select-none"
      style={{ width: size, height: size }}
    >
      {/* Outer segmented ring */}
      <div
        className={`absolute inset-0 rounded-full border-2 border-dashed ${config.ring} animate-orb-rotate-slow`}
      />
      {/* HUD arc bracket ring */}
      <div
        className={`absolute rounded-full border-2 border-t-cyan-400 border-r-transparent border-b-cyan-400 border-l-transparent animate-orb-rotate-med`}
        style={{ width: size * 0.82, height: size * 0.82 }}
      />
      {/* Middle tactical ring */}
      <div
        className="absolute rounded-full border border-cyan-400/60 animate-orb-pulse"
        style={{
          width: size * 0.68,
          height: size * 0.68,
          boxShadow: `0 0 15px ${config.glow}`,
        }}
      />
      {/* Inner fast ring (visible on listening/thinking) */}
      {(state === "listening" || state === "thinking") && (
        <div
          className="absolute rounded-full border border-cyan-300/70 animate-orb-rotate-fast"
          style={{ width: size * 0.55, height: size * 0.55 }}
        />
      )}
      {/* Ripple rings when speaking */}
      {state === "speaking" && (
        <>
          <div
            className="absolute rounded-full border border-cyan-400/50 animate-ring-ripple"
            style={{ width: size * 0.5, height: size * 0.5 }}
          />
          <div
            className="absolute rounded-full border border-cyan-400/40 animate-ring-ripple"
            style={{ width: size * 0.5, height: size * 0.5, animationDelay: "0.7s" }}
          />
        </>
      )}
      {/* Core */}
      <div
        className={`relative rounded-full bg-gradient-to-tr ${config.core} ${config.pulseClass} flex items-center justify-center`}
        style={{
          width: size * 0.4,
          height: size * 0.4,
          boxShadow: `0 0 60px ${config.glow}, inset 0 0 30px rgba(255,255,255,0.4)`,
          transform: `scale(${scale})`,
          transition: "transform 0.08s ease-out",
        }}
      >
        <div className="font-display text-white font-black text-xl text-glow tracking-widest">
          J.A.R.V.I.S.
        </div>
      </div>
    </div>
  );
}
